import { Stack, useDisclosure } from "@chakra-ui/react";
import SendConfig from "pages/OceanWeatherIntelligence/InsightAlert/Components/SendConfig";
import React, { useState, memo, useMemo } from "react";
import { CreateAlertFormValues } from "../types";
import { AlertCustomTemplate } from "pages/OceanWeatherIntelligence/InsightAlert/Components/AlertCustomTemplate";
import { AlertTemplateModal } from "pages/OceanWeatherIntelligence/InsightAlert/Components/AlertTemplateModal";
import { AlertDeliveryChannel } from "pages/OceanWeatherIntelligence/InsightAlert/Components/AlertDeliveryChannel";
import { AlertUserSelection } from "pages/OceanWeatherIntelligence/InsightAlert/Components/AlertUserSelection";
import { IOperationRuleAlertUser } from "stores/data/insightAlerts/insightAlertModel";
import { useStores } from "stores/setup/use-store";
import {
  useAlertCreationTemplateMessages,
  useAlertCreationTemplateCustomization,
  useAlertCreationStep2Handlers,
  useAlertCreationUsersSync,
  useUserSelectionPermissions,
} from "../hooks";
import { formatConditions } from "pages/OceanWeatherIntelligence/InsightAlert/util/formatConditionGroups";
import i18n from "../../../i18n";

interface Step3DeliveryConfigProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleChange: any;
  values: CreateAlertFormValues;
  t: (key: React.Key) => string;
  errors?: any;
  touched?: any;
  usersTableSelected?: React.Key[];
  setUsersTableSelected?: (keys: React.Key[]) => void;
  alertUsers?: IOperationRuleAlertUser[];
}

export const Step3DeliveryConfig: React.FC<Step3DeliveryConfigProps> = memo(
  ({
    setFieldValue,
    handleChange,
    values,
    t,
    errors,
    touched,
    usersTableSelected: propsUsersTableSelected,
    setUsersTableSelected: propsSetUsersTableSelected,
    alertUsers: propsAlertUsers,
  }) => {
    const {
      dataStores: { insightAlertStore },
    } = useStores();

    const [localUsersTableSelected, setLocalUsersTableSelected] = useState<
      React.Key[]
    >([]);

    const usersTableSelected =
      propsUsersTableSelected ?? localUsersTableSelected;
    const setUsersTableSelected =
      propsSetUsersTableSelected ?? setLocalUsersTableSelected;
    const alertUsers =
      propsAlertUsers ?? insightAlertStore.operationRuleAlertUser;

    const {
      isOpen: isAlertTemplateOpen,
      onOpen: onAlertTemplateOpen,
      onClose: onAlertTemplateClose,
    } = useDisclosure();

    const TEMPLATE_MESSAGES = useAlertCreationTemplateMessages(t);

    const {
      alertTitle,
      setAlertTitle,
      alertDescription,
      setAlertDescription,
      updateTemplateValues,
    } = useAlertCreationTemplateCustomization({
      values,
      setFieldValue,
      templateMessages: TEMPLATE_MESSAGES,
    });

    const { handleUsersSelectionChange } = useAlertCreationStep2Handlers({
      setFieldValue,
      setUsersTableSelected,
      updateTemplateValues,
    });

    useAlertCreationUsersSync({
      propsUsersTableSelected,
      values,
      setFieldValue,
    });

    const { isUserSelectionDisabled } = useUserSelectionPermissions({
      channels: values.channels,
    });

    // Get conditions from either conditionGroups (event creation flow) or selectedInsight (alert creation flow)
    const formattedConditions = useMemo(() => {
      // First, try to use conditionGroups from form values (used in event creation and standalone alerts)
      if (values.conditionGroups && values.conditionGroups.length > 0) {
        return formatConditions(
          values.conditionGroups,
          i18n.language as "pt-BR" | "en",
        );
      }

      // If no conditionGroups, check selectedInsight conditions (used in insight alert creation)
      const selectedInsight = insightAlertStore.selectedInsight;
      if (
        selectedInsight?.conditions &&
        selectedInsight.conditions.length > 0
      ) {
        // Convert ILocationMenuInsightCondition[] to ConditionGroup[] format
        const convertedConditions = selectedInsight.conditions.map((group) => ({
          id: group.id,
          parentId: group.parentId,
          conditions: group.conditions.map((cond) => ({
            environmentalVariable: cond.environmentalVariable,
            environmentalVariableId: cond.environmentalVariableId,
            value: cond.value,
            comparisonOperator: cond.comparisonOperator,
          })),
          groupOperator: group.groupOperator,
          conditionOperator: group.conditionOperator,
        }));

        return formatConditions(
          convertedConditions,
          i18n.language as "pt-BR" | "en",
        );
      }

      // No conditions available
      return formatConditions(undefined, i18n.language as "pt-BR" | "en");
    }, [values.conditionGroups, insightAlertStore.selectedInsight]);

    return (
      <>
        <AlertTemplateModal
          isOpen={isAlertTemplateOpen}
          onClose={onAlertTemplateClose}
          title={alertTitle}
          description={alertDescription}
          conditions={formattedConditions}
          area={values.area}
        />

        <Stack spacing={6}>
          <AlertDeliveryChannel
            t={t}
            values={values}
            setFieldValue={setFieldValue}
          />

          <SendConfig
            handleChange={handleChange}
            values={values}
            t={t}
            defaultOpen={true}
          />

          <AlertCustomTemplate
            t={t}
            alertTitle={alertTitle}
            alertDescription={alertDescription}
            setAlertTitle={setAlertTitle}
            setAlertDescription={setAlertDescription}
            handleChange={handleChange}
            onAlertTemplateOpen={onAlertTemplateOpen}
            errors={errors}
            touched={touched}
          />

          <AlertUserSelection
            t={t}
            alertUsers={alertUsers}
            usersTableSelected={usersTableSelected}
            setUsersTableSelected={setUsersTableSelected}
            setFieldValue={setFieldValue}
            operationRuleAlertUserSelected={
              insightAlertStore.operationRuleAlertUserSelected
            }
            setOperationRuleAlertUserSelected={(users) =>
              insightAlertStore.setOperationRuleAlertUserSelected(users)
            }
            onUsersSelectionChange={handleUsersSelectionChange}
            disabled={isUserSelectionDisabled}
          />
        </Stack>
      </>
    );
  },
);
